import { http } from '@/shared/api/axios'
import type {
  ClientEmployeePayRate,
  ReapplyResult,
  ResolvedPayRates,
  ShiftRates,
  SitePayRates,
  UpdateSitePayRatesRequest,
  UpsertClientEmployeePayRateRequest,
} from '../model/types'

const PAYROLL_BASE = '/v1/payroll'
const SHIFTS_BASE = '/v1/scheduler/shifts'

const quiet = { _skipErrorToast: true } as never

interface ReapplyPreviewResponse {
  shiftCount: number
}

const siteRatesUrl = (siteId: number) => `${PAYROLL_BASE}/sites/${siteId}/pay-rates`

const clientEmployeeRatesUrl = (clientId: number) =>
  `${PAYROLL_BASE}/clients/${clientId}/employee-pay-rates`

export const fetchSitePayRates = async (siteId: number): Promise<SitePayRates> => {
  const response = await http.get<SitePayRates>(siteRatesUrl(siteId), quiet)
  return response.data
}

export const updateSitePayRates = async (
  siteId: number,
  request: UpdateSitePayRatesRequest,
): Promise<ReapplyResult> => {
  const response = await http.put<ReapplyResult>(
    siteRatesUrl(siteId),
    request,
    quiet,
  )
  return response.data
}

export const fetchSitePayRatesPreview = async (
  siteId: number,
  applyFrom: string,
): Promise<number> => {
  const response = await http.get<ReapplyPreviewResponse>(
    `${siteRatesUrl(siteId)}/reapply-preview`,
    { params: { applyFrom }, _skipErrorToast: true } as never,
  )
  return response.data.shiftCount
}

export const fetchClientEmployeePayRates = async (
  clientId: number,
): Promise<ClientEmployeePayRate[]> => {
  const response = await http.get<ClientEmployeePayRate[]>(
    clientEmployeeRatesUrl(clientId),
    quiet,
  )
  return response.data
}

export const upsertClientEmployeePayRate = async (
  clientId: number,
  employeeUserId: number,
  request: UpsertClientEmployeePayRateRequest,
): Promise<ReapplyResult> => {
  const response = await http.put<ReapplyResult>(
    `${clientEmployeeRatesUrl(clientId)}/${employeeUserId}`,
    request,
    quiet,
  )
  return response.data
}

export const deleteClientEmployeePayRate = async (
  clientId: number,
  employeeUserId: number,
  applyFrom: string | null,
): Promise<ReapplyResult> => {
  const response = await http.delete<ReapplyResult>(
    `${clientEmployeeRatesUrl(clientId)}/${employeeUserId}`,
    { params: applyFrom ? { applyFrom } : undefined, _skipErrorToast: true } as never,
  )
  return response.data
}

export const fetchClientEmployeePayRatePreview = async (
  clientId: number,
  employeeUserId: number,
  applyFrom: string,
): Promise<number> => {
  const response = await http.get<ReapplyPreviewResponse>(
    `${clientEmployeeRatesUrl(clientId)}/${employeeUserId}/reapply-preview`,
    { params: { applyFrom }, _skipErrorToast: true } as never,
  )
  return response.data.shiftCount
}

export const fetchCompensationPreview = async (
  employeeUserId: number,
  applyFrom: string,
): Promise<number> => {
  const response = await http.get<ReapplyPreviewResponse>(
    `${PAYROLL_BASE}/employees/${employeeUserId}/compensation/reapply-preview`,
    { params: { applyFrom }, _skipErrorToast: true } as never,
  )
  return response.data.shiftCount
}

export const resolvePayRates = async (
  siteId: number,
  employeeUserIds: number[],
): Promise<ResolvedPayRates> => {
  const response = await http.get<ResolvedPayRates>(`${PAYROLL_BASE}/pay-rates/resolve`, {
    params: { siteId, employeeUserIds },
    paramsSerializer: { indexes: null },
    _skipErrorToast: true,
  } as never)
  return response.data
}

export const fetchShiftRates = async (shiftId: number): Promise<ShiftRates> => {
  const response = await http.get<ShiftRates>(`${SHIFTS_BASE}/${shiftId}/rates`, quiet)
  return response.data
}
